import { calculateAvgNightlyPrice } from "@@/carts/bookable-checkout/accommodation-cost";
import { useBookingAccommodationContext } from "@@/products/accommodations/booking-accommodation-context";
import { useAddAccommodationToCart } from "@@/products/accommodations/use-add-accommodation-to-cart";
import { darkeningGradientDualHeavierTopSide_80 } from "@@/shared/bg-darkening-gradient";
import { Conditional } from "@@/shared/conditional";
import { HorizontalDivider, VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { SectionAreaCard } from "@@/shared/section-area/section-area-card";
import {
    Paragraph,
    TextBox,
    paragraphHzSpaceFactory,
    paragraphIconFactory,
    paragraphPriceFactory,
} from "@@/shared/text";
import { useStorageItemImageReference } from "@@/storage-items/queries/use-storage-item-reference";
import { useTheme } from "@emotion/react";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import type {
    AccommodationInventory,
    CalculatedRatePrice,
    Price,
    Product,
    RemSize,
    Resource,
    ResourceAvailability,
    ResourceGraph,
    ResourceImageReference,
    TimeRange,
} from "@towni/common";
import {
    colorFactory,
    datesBetweenIso,
    getAccommodationRateOptionType,
    getPossibilities,
    isResourceAccommodation,
    isResourceGraph,
    isResourceGroup,
    isResourcesAccommodationsWithinCapacity,
    isTimeRange,
    makeResourceGraph,
    sum,
    translation,
    tz,
    uniqueBy,
    whiteColor,
} from "@towni/common";
import { useCallback, useMemo } from "react";
import { AccommodationInfoModalButton } from "./modals/accommodation-info.modal";
import { useAccommodationTimeRangeFormatter } from "./shared/use-accommodation-time-range-formatter";
// --- Props ---

type Props = {
    readonly product: Product;
    readonly resource: Resource;
    readonly allResources: Resource[];
    readonly calculatedRates: CalculatedRatePrice[];
    readonly range: TimeRange | undefined;
    readonly availability: ResourceAvailability[];
    readonly inventory: AccommodationInventory;
};

// --- Helper Functions & Hooks ---

/**
 * Performs a Depth-First Search (DFS) on a ResourceGraph
 * to find the first available set of images. It prioritizes
 * images from ResourceGroups first, then Accommodations.
 * @param graph - The ResourceGraph to search.
 * @returns An array of ResourceImageReference, or an empty array if none found.
 */
const getBestImagesFromGraph = (
    graph: ResourceGraph,
): ResourceImageReference[] => {
    const stack = [graph];

    while (stack.length > 0) {
        const currentGraph = stack.pop();
        if (!currentGraph) continue;

        const rgImages = currentGraph.resourceGroup.imageRefs ?? [];
        if (rgImages.length > 0) {
            return rgImages;
        }

        for (const child of currentGraph.children) {
            if (isResourceGraph(child)) {
                stack.push(child);
            } else if (
                isResourceAccommodation(child) &&
                child.imageRefs &&
                child.imageRefs.length > 0
            ) {
                return child.imageRefs; // Return immediately on first find
            }
        }
    }
    return []; // No images found
};

/**
 * A hook to get the primary image URL for a given resource.
 * It intelligently searches through resource groups if necessary.
 * @param resource - The resource (Group or Accommodation).
 * @param allResources - All available resources for graph construction.
 * @returns The URL of the first found image, or undefined.
 */
const useResourceImage = (resource: Resource, allResources: Resource[]) => {
    const images = useMemo(() => {
        if (isResourceGroup(resource)) {
            return getBestImagesFromGraph(
                makeResourceGraph(resource, allResources),
            );
        }
        if (isResourceAccommodation(resource)) {
            return resource.imageRefs ?? [];
        }
        return [];
    }, [resource, allResources]);

    // TODO: Handle multiple images selection/logic if needed.
    const firstImageId = images.length > 0 ? images[0].imageId : undefined;
    const [imageUrl] = useStorageItemImageReference(firstImageId);

    return imageUrl;
};

// --- Constants ---

const _cardColor = colorFactory({
    hue: 120,
    saturation: 0.24,
    light: 0.89,
});

type PaletteColor = {
    red: number;
    green: number;
    blue: number;
    hex: string;
};

// --- Main Component ---

const AccommodationCard = (props: Props) => {
    const {
        product,
        resource,
        allResources,
        calculatedRates,
        range,
        availability,
        inventory,
    } = props;

    const theme = useTheme();
    const formatTimeRange = useAccommodationTimeRangeFormatter(
        tz.EuropeStockholm,
    ); // Assumed to be provider's timezone

    // --- Context & Cart ---
    const {
        quantities,
        contextTimeRange,
        requestedQuantity,
        setRequestedResourceQuantity,
    } = useBookingAccommodationContext(state => ({
        quantities: state.quantities,
        contextTimeRange: state.timeRange, // Renamed for clarity
        requestedQuantity: state.requestedResourceQuantity,
        setRequestedResourceQuantity: state.setRequestedResourceQuantity,
    }));

    const [addToCart, currentPriceFn] = useAddAccommodationToCart(product);

    // --- Basic Resource Info ---
    const { title, kicker, subtitle } = resource.meta;
    const imageUrl = useResourceImage(resource, allResources);

    const availableResources = useMemo(
        () =>
            uniqueBy(
                availability.flatMap(item => item.availableSubResources),
                r => r._id,
            ),
        [availability],
    );

    const availableAccommodations = useMemo(
        () => availableResources.filter(isResourceAccommodation),
        [availableResources],
    );

    const resourcesWithCapacityResult = useMemo(
        () =>
            isResourcesAccommodationsWithinCapacity(
                availableAccommodations,
                requestedQuantity,
                quantities,
            ),
        [availableAccommodations, requestedQuantity, quantities],
    );

    const resourcesWithCapacity = useMemo(
        () =>
            resourcesWithCapacityResult
                .filter(r => !r.error)
                .map(r => r.resource),
        [resourcesWithCapacityResult],
    );

    const possibilities = useMemo(
        () =>
            getPossibilities({
                availableResources: availableAccommodations,
                numberOfResources: requestedQuantity,
                requestedQuantities: quantities,
            }),
        [availableAccommodations, requestedQuantity, quantities],
    );

    const availableResourceCount = useMemo(
        () => sum(availability.map(item => item.availableSubResources.length)),
        [availability],
    );

    const availableResourcesWithCapacityCount = useMemo(
        () =>
            sum(
                availability.map(
                    item =>
                        item.availableSubResources.filter(sub =>
                            resourcesWithCapacity.some(
                                rwc => rwc._id === sub._id,
                            ),
                        ).length,
                ),
            ),
        [availability, resourcesWithCapacity],
    );

    const hasQuantityWithCapacity = useMemo(
        () => requestedQuantity <= availableResourcesWithCapacityCount,
        [requestedQuantity, availableResourcesWithCapacityCount],
    );

    // --- Price Calculation (Memoized) ---

    const matchingRates = useMemo(
        () =>
            calculatedRates.filter(r =>
                r.rateWithPrices.appliesTo.includes(resource._id),
            ),
        [calculatedRates, resource._id],
    );

    const currentPriceOfBookingFunc = useCallback(
        (quantity: number) => {
            if (!matchingRates.length || !range) return undefined;

            const isTotalPrice = !matchingRates.every(r =>
                r.rateWithPrices.options.every(
                    o =>
                        getAccommodationRateOptionType(o.applies) ===
                        "PER_NIGHT",
                ),
            );

            const resourceAccommodationWithEnforcedCapacityHash = Array.from(
                possibilities.resourcesWithHashMapped.values(),
            );

            const current =
                isResourceGroup(resource) && isTimeRange(contextTimeRange)
                    ? currentPriceFn({
                          time: contextTimeRange,
                          resource: resource,
                          rates: matchingRates,
                          inventory: inventory,
                          quantity: quantity,
                          resourceAccommodationWithEnforcedCapacityHash,
                      })
                    : undefined;

            if (!current) return undefined;

            const dates = datesBetweenIso(range.start, range.end);
            const nights = Math.max(0, dates.length - 1); // Ensure non-negative

            if (isTotalPrice) {
                return { price: current, isTotalPrice, nights };
            }

            const [avgNightlyPrice] = calculateAvgNightlyPrice(current, range);
            return { price: avgNightlyPrice, isTotalPrice: false, nights };
        },
        [
            matchingRates,
            range,
            possibilities.resourcesWithHashMapped,
            resource,
            contextTimeRange,
            currentPriceFn,
            inventory,
        ],
    );

    const currentPriceOfBooking = useMemo(() => {
        return currentPriceOfBookingFunc(requestedQuantity);
    }, [currentPriceOfBookingFunc, requestedQuantity]);

    // --- Booking Logic ---

    const canBePicked =
        hasQuantityWithCapacity &&
        !!currentPriceOfBooking &&
        availableResourcesWithCapacityCount >= requestedQuantity;

    const onBookClick = useMemo(
        () =>
            canBePicked
                ? () => {
                      if (
                          !isResourceGroup(resource) ||
                          !isTimeRange(contextTimeRange)
                      )
                          return;

                      const resourceAccommodationWithEnforcedCapacityHash =
                          Array.from(
                              possibilities.resourcesWithHashMapped.values(), // Reuse memoized possibilities
                          );

                      addToCart({
                          time: contextTimeRange,
                          resource: resource,
                          rates: matchingRates,
                          inventory: inventory,
                          quantity: requestedQuantity,
                          resourceAccommodationWithEnforcedCapacityHash,
                      });
                  }
                : undefined, // Explicitly undefined if cannot be picked
        [
            canBePicked,
            resource,
            contextTimeRange,
            possibilities.resourcesWithHashMapped,
            addToCart,
            matchingRates,
            inventory,
            requestedQuantity,
        ],
    );

    // --- UI State & Styling ---

    const hasBackgroundImage = !!imageUrl;

    const textColor = hasBackgroundImage
        ? theme.colors.white
        : theme.colors.black;
    const dimmedTextColor = hasBackgroundImage
        ? theme.colors.white.main.light70
        : theme.colors.black.light30;
    const textShadow = hasBackgroundImage
        ? "0 2px 5px rgba(0, 0, 0, 0.4)"
        : undefined;

    // --- Helper Functions for Rendering ---

    const getAvailabilityText = () => {
        if (!range) return translation({ sv: "Välj datum", en: "Select date" });
        if (availableResourceCount === 0)
            return translation({
                sv: "Inget ledigt",
                en: "No availability",
            });
        return formatTimeRange(range);
        // if (availableResourceCount === 0)
        //     return translation({
        //         sv: "Inget ledigt på valt datum",
        //         en: "No availability on selected date",
        //     });
        // if (!hasQuantityWithCapacity)
        //     return translation({
        //         sv: "Ingen matchning för valt antal gäster",
        //         en: "Not available for selected number of guests",
        //     });
        // return translation({
        //     sv: `Går inte att boka ${requestedQuantity} st`,
        //     en: `Cannot book ${requestedQuantity} accommodations`,
        // });
    };

    const renderPriceInfo = (currentPriceOfBooking?: {
        price: Price;
        isTotalPrice: boolean;
        nights: number;
    }) => {
        if (!currentPriceOfBooking) {
            if (availableResourceCount < requestedQuantity) {
                if (range === undefined) {
                    return [
                        translation({
                            sv: "Välj datum för att se tillgänglighet",
                            en: "Select date to see availability",
                        }),
                    ];
                }

                return [
                    translation({
                        sv: "För få lediga enheter tillgängliga",
                        en: "Not enough units available",
                    }),
                ];
            }

            return [
                translation({
                    sv: "Ej bokningsbar för valt sökkriterium ",
                    en: "Not bookable for selected search criteria ",
                }),
            ];
        }

        const { price, isTotalPrice, nights } = currentPriceOfBooking;

        if (isTotalPrice) {
            return [
                paragraphPriceFactory({ price }),
                translation({
                    sv: ` för ${nights} ${nights > 1 ? "nätter" : "natt"}`,
                    en: ` for ${nights} ${nights > 1 ? "nights" : "night"}`,
                }),
            ];
        }

        return [
            paragraphPriceFactory({ price }),
            " / ",
            translation({ sv: "natt", en: "night" }),
        ];
    };

    const getBookingButtonText = () => {
        if (!canBePicked) {
            return availableResourceCount === 0
                ? translation({ sv: "Utsålt", en: "Sold out" })
                : translation({ sv: "-", en: "-" });
        }
        return requestedQuantity > 1
            ? translation({
                  sv: `Boka (${requestedQuantity} st)`,
                  en: `Book (${requestedQuantity} pcs)`,
              })
            : translation({ sv: "Boka", en: "Book" });
    };

    // --- TODO: Refactor `howManyResourcesAreNeeded` ---
    const howManyResourcesAreNeeded = useMemo(() => {
        if (hasQuantityWithCapacity) return requestedQuantity;
        if (availableResourceCount === 0) return undefined;

        for (let i = requestedQuantity + 1; i < requestedQuantity + 10; i++) {
            const tempResult = isResourcesAccommodationsWithinCapacity(
                availableAccommodations,
                i,
                quantities,
            );
            const tempCapacity = tempResult
                .filter(r => !r.error)
                .map(r => r.resource);
            const tempAvailableCount = sum(
                availability.map(
                    item =>
                        item.availableSubResources.filter(sub =>
                            tempCapacity.some(rwc => rwc._id === sub._id),
                        ).length,
                ),
            );
            if (i <= tempAvailableCount) return i;
        }
        return undefined;
    }, [
        hasQuantityWithCapacity,
        requestedQuantity,
        availableResourceCount,
        availableAccommodations,
        quantities,
        availability,
    ]);
    const priceOfBookingIfHigher = useMemo(() => {
        return currentPriceOfBookingFunc(
            howManyResourcesAreNeeded ?? requestedQuantity,
        );
    }, [
        currentPriceOfBookingFunc,
        howManyResourcesAreNeeded,
        requestedQuantity,
    ]);
    const resourcesNeededForSuggestion = howManyResourcesAreNeeded;

    // Also assuming you have access to 'quantities' to show the guest count
    const totalGuests = quantities.ADULT + quantities.CHILD || quantities.GUEST;

    const needMoreUnits =
        !!resourcesNeededForSuggestion &&
        requestedQuantity !== resourcesNeededForSuggestion;

    return (
        <>
            <SectionAreaCard
                aspectRatio={needMoreUnits ? "1:1" : "4:3"} // Adjust aspect ratio based on resources needed
                radius={10}
                onClick={onBookClick}
                background={{
                    color: _cardColor,
                    fit: "COVER",
                    repeat: "NO-REPEAT",
                    imageReference: imageUrl,
                }}
                css={{
                    borderRadius: 10,
                    overflow: "hidden",
                    opacity: 1, // Consider if opacity needs to change based on state
                    cursor: onBookClick ? "pointer" : "default", // Add cursor feedback
                }}>
                <FlexColumn
                    fillParent
                    tag="ACCOMMODATION-CARD"
                    background={
                        imageUrl
                            ? {
                                  linearGradient:
                                      darkeningGradientDualHeavierTopSide_80,
                              }
                            : undefined
                    }
                    mainAxis="space-between"
                    crossAxis="stretch"
                    css={{
                        flexShrink: 0,
                        flexGrow: 1,
                        padding: 20,
                    }}>
                    {/* Card title and kicker */}
                    <FlexRow
                        fillParentWidth
                        mainAxis="space-between"
                        crossAxis="flex-start"
                        css={{ flexShrink: 0 }}>
                        <FlexColumn>
                            <TextBox
                                text={kicker}
                                size={0.75 as RemSize}
                                color={textColor}
                                css={{ textShadow }}
                                weight="800"
                            />
                            <TextBox
                                text={title}
                                weight="700"
                                size="XL"
                                color={textColor}
                                css={{ textShadow }}
                            />
                            <VerticalDivider XXS />
                            <TextBox
                                text={subtitle}
                                size={1}
                                color={textColor}
                                css={{ textShadow }}
                                className="line-clamp-1 overflow-hidden text-ellipsis"
                            />
                        </FlexColumn>
                        <HorizontalDivider />
                        <AccommodationInfoModalButton
                            resource={resource}
                            allResources={allResources}
                        />
                    </FlexRow>

                    {/* Availability and price info */}
                    <FlexColumn
                        fillParentWidth
                        mainAxis="flex-end"
                        css={{ flex: 1 }}>
                        <FlexRow
                            fillParentWidth
                            mainAxis="flex-end"
                            crossAxis="stretch">
                            <Conditional
                                when={needMoreUnits}
                                else={() => (
                                    <>
                                        <FlexColumn
                                            mainAxis="center"
                                            crossAxis="flex-end">
                                            <TextBox
                                                color={textColor}
                                                weight="600"
                                                align="right"
                                                text={getAvailabilityText()}
                                            />
                                            <Paragraph
                                                css={{
                                                    fontWeight: 600,
                                                    color: dimmedTextColor.asString,
                                                }}
                                                content={renderPriceInfo(
                                                    currentPriceOfBooking,
                                                )}
                                            />
                                        </FlexColumn>
                                        <HorizontalDivider M />
                                        {/* Booking button */}
                                        <Conditional
                                            when={
                                                canBePicked &&
                                                availableResourceCount !== 0
                                            }>
                                            <FlexColumn
                                                mainAxis="center"
                                                background={{
                                                    color: hasBackgroundImage
                                                        ? theme.colors.black.withAlpha(
                                                              0.2,
                                                          )
                                                        : theme.colors
                                                              .transparent,
                                                }}
                                                padding={{
                                                    leftRight:
                                                        availableResourcesWithCapacityCount <=
                                                        1
                                                            ? 32
                                                            : 20,
                                                    topBottom: 12,
                                                }}
                                                css={{
                                                    borderRadius: theme.radius,
                                                    border: `1px solid ${textColor.asString}`,
                                                    backdropFilter: "blur(5px)",
                                                }}>
                                                <TextBox
                                                    text={getBookingButtonText()}
                                                    size="S"
                                                    weight="600"
                                                    align="center"
                                                    color={textColor}
                                                    css={{
                                                        textShadow,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                />
                                            </FlexColumn>
                                        </Conditional>
                                    </>
                                )}>
                                <FlexColumn fillParentWidth>
                                    <FlexColumn
                                        padding={{
                                            topBottom: 5,
                                            leftRight: 10,
                                        }}
                                        css={{
                                            borderRadius: theme.radius,
                                            borderLeft: `5px solid ${theme.colors.information.asString}`,
                                            gap: 8,
                                            backgroundColor:
                                                theme.colors.black.withAlpha(
                                                    0.2,
                                                ).asString,
                                            backdropFilter: "blur(5px)",
                                        }}>
                                        <FlexColumn
                                            mainAxis="center"
                                            crossAxis="flex-start">
                                            <Paragraph
                                                css={{
                                                    fontWeight: 600,
                                                    color: whiteColor.asString,
                                                }}
                                                content={[
                                                    paragraphIconFactory({
                                                        icon: faCircleInfo,
                                                    }),
                                                    paragraphHzSpaceFactory(),
                                                    {
                                                        sv: "Smart anpassning:",
                                                        en: "Smart booking:",
                                                    },
                                                ]}
                                            />

                                            <Paragraph
                                                css={{
                                                    fontWeight: 600,
                                                    color: whiteColor.asString,
                                                    fontSize: "0.9rem",
                                                }}
                                                content={{
                                                    sv: `För ${totalGuests} gäster behövs minst ${resourcesNeededForSuggestion} enheter bokas.`,
                                                    en: `For ${totalGuests} guests, ${resourcesNeededForSuggestion} units are needed`,
                                                }}
                                            />
                                        </FlexColumn>
                                    </FlexColumn>
                                    <VerticalDivider M />
                                    <FlexRow
                                        mainAxis="flex-end"
                                        crossAxis="stretch">
                                        <FlexColumn
                                            mainAxis="center"
                                            crossAxis="flex-end">
                                            <TextBox
                                                color={textColor}
                                                weight="600"
                                                align="right"
                                                text={getAvailabilityText()}
                                            />
                                            <Paragraph
                                                css={{
                                                    fontWeight: 600,
                                                    color: dimmedTextColor.asString,
                                                }}
                                                content={renderPriceInfo(
                                                    priceOfBookingIfHigher,
                                                )}
                                            />
                                        </FlexColumn>
                                        <HorizontalDivider M />
                                        {/* Booking button */}

                                        <FlexColumn
                                            onClick={() => {
                                                if (
                                                    !resourcesNeededForSuggestion
                                                )
                                                    return;

                                                if (
                                                    isResourceGroup(resource) &&
                                                    isTimeRange(
                                                        contextTimeRange,
                                                    )
                                                ) {
                                                    const resourceAccommodationWithEnforcedCapacityHash =
                                                        Array.from(
                                                            possibilities.resourcesWithHashMapped.values(), // Reuse memoized possibilities
                                                        );

                                                    addToCart({
                                                        time: contextTimeRange,
                                                        resource: resource,
                                                        rates: matchingRates,
                                                        inventory: inventory,
                                                        quantity:
                                                            resourcesNeededForSuggestion,
                                                        resourceAccommodationWithEnforcedCapacityHash,
                                                    });
                                                }

                                                setRequestedResourceQuantity(
                                                    resourcesNeededForSuggestion,
                                                );
                                            }}
                                            mainAxis="center"
                                            background={{
                                                color: hasBackgroundImage
                                                    ? theme.colors.black.withAlpha(
                                                          0.2,
                                                      )
                                                    : theme.colors.transparent,
                                            }}
                                            padding={{
                                                leftRight:
                                                    availableResourcesWithCapacityCount <=
                                                    1
                                                        ? 32
                                                        : 20,
                                                topBottom: 12,
                                            }}
                                            css={{
                                                borderRadius: theme.radius,
                                                border: `1px solid ${textColor.asString}`,
                                                backdropFilter: "blur(5px)",
                                            }}>
                                            <TextBox
                                                text={{
                                                    sv: `Boka ${resourcesNeededForSuggestion} st`,
                                                    en: `Book ${resourcesNeededForSuggestion} pcs`,
                                                }}
                                                size="S"
                                                weight="600"
                                                align="center"
                                                color={textColor}
                                                css={{
                                                    textShadow,
                                                    whiteSpace: "nowrap",
                                                }}
                                            />
                                        </FlexColumn>
                                    </FlexRow>
                                </FlexColumn>
                            </Conditional>
                        </FlexRow>
                    </FlexColumn>
                </FlexColumn>
            </SectionAreaCard>
        </>
    );
};

export { AccommodationCard, useResourceImage }; // Keep `useResourceImage` exported if used elsewhere
